import type { FishStockLog } from '../../types/production'
import type { BiomassDataRow } from '../../utils/productionUnitTable'

import {
  buildDailyFishStockRollupKey,
  rollupFishStockLogsByLotDay,
  type DailyFishStockRollup
} from '../../utils/dailyFishStockRollup'
import { toLocalCalendarDayKey } from '../../utils/dateNormalization'
import { farmTimeZone } from '../../utils/farmTime'

type BiomassFishStockMetadata = {
  productionRowId?: string
  lotId?: string
  stage?: 'Active' | 'Inactive'
}

const toTrimmedString = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  return trimmed || undefined
}

/**
 * Reverse of `buildBiomassFishStockMetadata`: extract the biomass-linkage
 * fields from a `FishStockLog.metadata` payload. Returns an empty object
 * when the metadata is missing, malformed, or lacks the expected keys.
 */
export const parseBiomassFishStockMetadata = (log: FishStockLog): BiomassFishStockMetadata => {
  const metadata = log.metadata
  if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) return {}

  const stage = toTrimmedString(metadata.stage)
  let normalizedStage: 'Active' | 'Inactive' | undefined
  if (stage === 'Inactive') normalizedStage = 'Inactive'
  else if (stage === 'Active') normalizedStage = 'Active'

  return {
    productionRowId: toTrimmedString(metadata.productionRowId),
    lotId: toTrimmedString(metadata.lotId),
    stage: normalizedStage
  }
}

const toCalendarDateKey = (value: string | undefined | null, timeZone?: string): string => {
  // Local-day keying matches `overlayMortalityLogsOnProductionRows` and
  // `overlayWeightSamplesOnProductionRows`. Without this, evening rows in
  // behind-UTC time zones split off into their own bucket and the FSL trust
  // signal disagrees with what users see on the page.
  return toLocalCalendarDayKey(value, timeZone)
}

const buildRollupKeyForRow = (rowDate: string, lot: string | undefined, timeZone?: string): string => {
  return buildDailyFishStockRollupKey(toCalendarDateKey(rowDate, timeZone), String(lot ?? '').trim())
}

/**
 * Build the metadata payload written to a `FishStockLog` at save time so the
 * read side can promote the log captured *for* this row above the other
 * contributing logs. Mirrors `buildBiomassSamplingWeightMetadata`.
 *
 * `source: 'biomassFishStock'` distinguishes fish-stock events tagged by the
 * biomass surface from those tagged by other surfaces (Sampling/Conteo)
 * so downstream filters can pick their preferred provenance.
 */
export const buildBiomassFishStockMetadata = (args: {
  productionRowId: string
  lotId: string
  stage: 'Active' | 'Inactive'
}): Record<string, unknown> => ({
  source: 'biomassFishStock',
  productionRowId: args.productionRowId,
  lotId: args.lotId,
  stage: args.stage
})

/**
 * Find the single `FishStockLog` linked to this row for the
 * "edit-this-event" affordance. Prefers a metadata-tagged match (the log
 * captured *for* this row), then falls back to the first log matching
 * `(calendar date, lot)`.
 *
 * Intentionally 1:1. For the canonical *daily fish stock* (which is
 * latest-value-wins across every event on the same day for the same lot)
 * use `findAllLinkedFishStockLogsForRow` or read `row.fishStock` after
 * `overlayFishStockOnProductionRows`.
 */
export const findLinkedFishStockLog = (
  row: Pick<BiomassDataRow, 'id' | 'date' | 'lot'>,
  logs: FishStockLog[]
): FishStockLog | undefined => {
  const rowDateKey = toCalendarDateKey(row.date)
  if (!rowDateKey) {
    return logs.find((log) => parseBiomassFishStockMetadata(log).productionRowId === row.id)
  }

  const rollupKey = buildRollupKeyForRow(row.date, row.lot)
  let fallbackMatch: FishStockLog | undefined

  for (const log of logs) {
    const metadata = parseBiomassFishStockMetadata(log)
    if (metadata.productionRowId === row.id) return log
    if (!fallbackMatch && buildRollupKeyForRow(log.recordedAt, log.lot, farmTimeZone(log.farmId)) === rollupKey) {
      fallbackMatch = log
    }
  }

  return fallbackMatch
}

/**
 * Return every `FishStockLog` whose `(calendar date, lot)` matches this row,
 * in input order, with the metadata-tagged log promoted to the front when
 * one exists. Used by trust-signal UI ("derived from N events") and any
 * consumer that needs per-event detail beyond the aggregated latest count.
 */
export const findAllLinkedFishStockLogsForRow = (
  row: Pick<BiomassDataRow, 'id' | 'date' | 'lot'>,
  logs: FishStockLog[]
): FishStockLog[] => {
  const rowDateKey = toCalendarDateKey(row.date)
  if (!rowDateKey) return []

  const rollupKey = buildRollupKeyForRow(row.date, row.lot)
  const matched: FishStockLog[] = []
  let metadataMatchIndex = -1

  for (const log of logs) {
    if (buildRollupKeyForRow(log.recordedAt, log.lot, farmTimeZone(log.farmId)) !== rollupKey) continue
    const metadata = parseBiomassFishStockMetadata(log)
    if (metadataMatchIndex === -1 && metadata.productionRowId === row.id) {
      metadataMatchIndex = matched.length
    }
    matched.push(log)
  }

  if (metadataMatchIndex > 0) {
    const [primary] = matched.splice(metadataMatchIndex, 1)
    matched.unshift(primary)
  }

  return matched
}

const pickPrimaryLogForRow = (
  rowId: string,
  rollup: DailyFishStockRollup,
  logsById: Map<string, FishStockLog>
): FishStockLog | undefined => {
  // Prefer a metadata-tagged log for this row, then the most recently
  // recorded contributing log so the "edit this event" affordance points at
  // something operationally meaningful.
  for (const logId of rollup.logIds) {
    const log = logsById.get(logId)
    if (!log) continue
    if (parseBiomassFishStockMetadata(log).productionRowId === rowId) return log
  }
  for (let i = rollup.logIds.length - 1; i >= 0; i -= 1) {
    const log = logsById.get(rollup.logIds[i])
    if (log) return log
  }
  return undefined
}

/**
 * Overlay the canonical daily fish-stock count onto each production row.
 *
 * For every row that has at least one matching `FishStockLog` (matched by
 * `(calendar date, lot)`), the row's `fishStock` is replaced with the
 * LATEST `countedStock` on that day (parallel to the WSL overlay resolving
 * v2.1 finding #60: events are canonical when they exist; the stored
 * `ProductionRecord.fishStock` becomes display-derived only, matching how
 * `overlayMortalityLogsOnProductionRows` treats `dailyMortality`).
 *
 * The row also carries:
 * - `fishStockLogId` — id of the metadata-tagged log for that row, or the
 *   most recent contributing log when no metadata-tagged log exists. Anchors
 *   the "edit this event" affordance.
 * - `fishStockLogCount` — string-encoded number of contributing logs. UI
 *   consumers parse this to render the "derived from N events" trust badge.
 *
 * Rows without matching logs are returned untouched (referential equality
 * preserved) — critical for React memoization in the chained overlay
 * pipeline shared with MORT-12 and WSL.
 */
export const overlayFishStockOnProductionRows = (
  rows: BiomassDataRow[],
  logs: FishStockLog[]
): BiomassDataRow[] => {
  if (rows.length === 0 || logs.length === 0) return rows

  const rollupByKey = rollupFishStockLogsByLotDay(logs)
  if (rollupByKey.size === 0) return rows

  const logsById = new Map<string, FishStockLog>()
  for (const log of logs) {
    if (typeof log.id === 'string' && log.id) {
      logsById.set(log.id, log)
    }
  }

  let changed = false
  const nextRows = rows.map((row) => {
    const key = buildRollupKeyForRow(row.date, row.lot)
    const rollup = rollupByKey.get(key)
    if (!rollup) return row

    const primaryLog = pickPrimaryLogForRow(row.id, rollup, logsById)
    if (!primaryLog) return row

    const metadata = parseBiomassFishStockMetadata(primaryLog)
    const nextFishStock = rollup.latestCountedStock
    const nextLotId = row.lotId ?? metadata.lotId
    const nextStage = metadata.stage ?? row.stage
    const nextFishStockLogId = primaryLog.id
    const nextFishStockLogCount = String(rollup.eventCount)
    // Findings #186: the method comes from the same log as the stock, so a
    // corrected Método shows (Biomasa vocabulary: exact / estimate).
    const nextStockMethod = rollup.latestMethod === 'estimation' ? 'estimate' : 'exact'
    const rowStockMethod = (row as { stockMethod?: unknown }).stockMethod
    // Findings #203: the moment the count stands for, for the stock rule.
    const nextCountedAtMs = rollup.latestCountAtMs !== null ? String(rollup.latestCountAtMs) : undefined

    if (
      row.fishStock === nextFishStock
      && row.lotId === nextLotId
      && row.stage === nextStage
      && row.fishStockLogId === nextFishStockLogId
      && row.fishStockLogCount === nextFishStockLogCount
      && rowStockMethod === nextStockMethod
      && row.fishStockCountedAtMs === nextCountedAtMs
    ) {
      return row
    }

    changed = true
    return {
      ...row,
      fishStock: nextFishStock,
      lotId: nextLotId,
      stage: nextStage,
      fishStockLogId: nextFishStockLogId,
      fishStockLogCount: nextFishStockLogCount,
      stockMethod: nextStockMethod,
      fishStockCountedAtMs: nextCountedAtMs
    }
  })

  return changed ? nextRows : rows
}

/**
 * Sidecar map from production-row id to the canonical event-derived fish-
 * stock rollup for that `(calendar date, lot)`. Returned without mutating
 * the input rows so screens that need to keep `data` as their editable
 * source of truth (notably the Database screen) can render trust signals
 * next to the stored value instead of overwriting it.
 *
 * Rows whose `(calendar date, lot)` does not match any log are omitted.
 */
export const computeFishStockRollupByRowId = (
  rows: Pick<BiomassDataRow, 'id' | 'date' | 'lot'>[],
  logs: FishStockLog[]
): Map<string, {
  latestCountedStock: string
  eventCount: number
  primaryLogId: string | undefined
  latestMethod: 'count' | 'estimation'
}> => {
  const out = new Map<string, {
    latestCountedStock: string
    eventCount: number
    primaryLogId: string | undefined
    latestMethod: 'count' | 'estimation'
  }>()
  if (rows.length === 0 || logs.length === 0) return out

  const rollupByKey = rollupFishStockLogsByLotDay(logs)
  if (rollupByKey.size === 0) return out

  const logsById = new Map<string, FishStockLog>()
  for (const log of logs) {
    if (typeof log.id === 'string' && log.id) logsById.set(log.id, log)
  }

  for (const row of rows) {
    const key = buildRollupKeyForRow(row.date, row.lot)
    const rollup = rollupByKey.get(key)
    if (!rollup || rollup.eventCount === 0) continue
    const primary = pickPrimaryLogForRow(row.id, rollup, logsById)
    out.set(row.id, {
      latestCountedStock: rollup.latestCountedStock,
      eventCount: rollup.eventCount,
      primaryLogId: primary?.id,
      latestMethod: rollup.latestMethod
    })
  }

  return out
}
